// src/components/audit/AuditTimelineViewer.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Avatar,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  alpha,
  Button
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Person,
  Business,
  Inventory,
  LocalShipping,
  Assignment,
  Info,
  Schedule,
  Visibility,
  TrendingUp,
  Circle,
  Refresh
} from '@mui/icons-material';
import { formatDistanceToNow, format, isSameDay } from 'date-fns';
import { ParsedAuditLog, AuditTimelineItem, AuditLogFilters } from '../../types/Audit.types';
import { AuditService } from '../../services/audit-service';
import StatusCard from '../StatusCard';

interface AuditTimelineViewerProps {
  entityType?: string;
  entityId?: string;
  apiEndpoint?: string;
  title?: string;
  maxHeight?: number | string;
  showDateHeaders?: boolean;
  compactMode?: boolean;
  onItemClick?: (item: AuditTimelineItem) => void;
  filters?: AuditLogFilters;
  refreshKey?: number;
  autoRefresh?: boolean;
}

const AuditTimelineViewer: React.FC<AuditTimelineViewerProps> = ({
  entityType,
  entityId,
  apiEndpoint,
  title = 'Activity Timeline',
  maxHeight = 600,
  showDateHeaders = true,
  compactMode = false,
  onItemClick,
  filters = {},
  refreshKey = 0,
  autoRefresh = false
}) => {
  const theme = useTheme();
  const [timelineItems, setTimelineItems] = useState<AuditTimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [lastRefreshKey, setLastRefreshKey] = useState(refreshKey);

  // Use refs to prevent stale closure issues and manage component lifecycle
  const isMountedRef = useRef(true);
  const loadingRequestRef = useRef<AbortController | null>(null);

  // Memoized functions to prevent unnecessary re-renders
  const getEntityIcon = useCallback((entityType: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      Shipment: <LocalShipping sx={{ fontSize: 18 }} />,
      Product: <Inventory sx={{ fontSize: 18 }} />,
      Employee: <Person sx={{ fontSize: 18 }} />,
      Supplier: <Business sx={{ fontSize: 18 }} />,
      Box: <Assignment sx={{ fontSize: 18 }} />,
      Warehouse: <Business sx={{ fontSize: 18 }} />,
      StorageUnit: <Business sx={{ fontSize: 18 }} />,
      Brand: <Business sx={{ fontSize: 18 }} />,
      ProductType: <Assignment sx={{ fontSize: 18 }} />
    };
    return iconMap[entityType] || <Info sx={{ fontSize: 18 }} />;
  }, []);

  const getActionIcon = useCallback((action: number) => {
    const iconMap: Record<number, React.ReactElement> = {
      1: <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />, // Create
      2: <Info sx={{ fontSize: 16, color: 'info.main' }} />, // Update
      3: <Circle sx={{ fontSize: 16, color: 'error.main' }} />, // Delete
      5: <LocalShipping sx={{ fontSize: 16, color: 'success.main' }} />, // Receive
      10: <Assignment sx={{ fontSize: 16, color: 'secondary.main' }} /> // Bulk
    };
    return iconMap[action] || <Info sx={{ fontSize: 16 }} />;
  }, []);

  const loadTimelineData = useCallback(async (forceRefresh = false) => {
    // Cancel any pending request
    if (loadingRequestRef.current) {
      loadingRequestRef.current.abort();
    }

    // Create new abort controller
    loadingRequestRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      let logs: ParsedAuditLog[];

      if (apiEndpoint) {
        const response = await fetch(apiEndpoint, {
          signal: loadingRequestRef.current.signal
        });
        if (!response.ok) throw new Error('Failed to fetch timeline data');
        const rawLogs = await response.json();
        logs = rawLogs.map((log: any) => AuditService.parseAuditLog(log));
      } else if (entityType && entityId) {
        logs = await AuditService.getEntityAuditLogs(
          entityType, 
          entityId, 
          1, 
          50, 
          !forceRefresh // use cache unless force refresh
        );
      } else {
        // Apply filters to timeline view
        const response = await AuditService.getAuditLogs(
          filters, 
          1, 
          50, 
          !forceRefresh
        );
        logs = response.items as unknown as ParsedAuditLog[];
      }

      const timeline = AuditService.convertToTimelineItems(logs);
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setTimelineItems(timeline.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
      }
    } catch (err) {
      // Don't show error if request was aborted (component unmounted)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load timeline data');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      loadingRequestRef.current = null;
    }
  }, [entityType, entityId, apiEndpoint, filters]);

  // Effect for initial load and key dependency changes
  useEffect(() => {
    loadTimelineData();
  }, [entityType, entityId]); // Only essential deps to prevent infinite loop

  // Effect for refresh key changes
  useEffect(() => {
    if (refreshKey !== lastRefreshKey) {
      setLastRefreshKey(refreshKey);
      loadTimelineData(true); // Force refresh when refresh key changes
    }
  }, [refreshKey, lastRefreshKey, loadTimelineData]);

  // Effect for filters changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        loadTimelineData();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters]); // Only filters in deps

  // Auto-refresh effect (if enabled)
  useEffect(() => {
    if (!autoRefresh) return;

    const intervalId = setInterval(() => {
      if (isMountedRef.current) {
        loadTimelineData(true);
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [autoRefresh, loadTimelineData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (loadingRequestRef.current) {
        loadingRequestRef.current.abort();
      }
    };
  }, []);

  const toggleExpanded = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const handleRefresh = useCallback(() => {
    loadTimelineData(true);
  }, [loadTimelineData]);

  const renderTimelineItem = useCallback((item: AuditTimelineItem, index: number) => {
    const isExpanded = expandedItems.has(item.id);
    const actionColor = AuditService.getActionColor(item.action);
    const showDateHeader = showDateHeaders && 
      (index === 0 || !isSameDay(item.timestamp, timelineItems[index - 1].timestamp));

    const hasDetails = (item.changes && item.changes.length > 0) || 
                      (item.metadata?.InfoData && Object.keys(item.metadata.InfoData).length > 0);

    return (
      <Fade in={true} timeout={(index + 1) * 100} key={item.id}>
        <Box>
          {showDateHeader && (
            <Box sx={{ mb: 3, mt: index > 0 ? 4 : 2 }}>
              <Box sx={{ position: 'relative', textAlign: 'center' }}>
                <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.3) }} />
                <Chip 
                  label={format(item.timestamp, 'EEEE, MMMM d, yyyy')} 
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ 
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 600,
                    px: 2,
                    py: 0.5
                  }}
                />
              </Box>
            </Box>
          )}

          <Box sx={{ position: 'relative', mb: 3 }}>
            {/* Timeline Line */}
            {index < timelineItems.length - 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 20,
                  top: 48,
                  bottom: -12,
                  width: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  zIndex: 0
                }}
              />
            )}

            {/* Timeline Item */}
            <Stack direction="row" spacing={2}>
              {/* Timeline Dot */}
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: `${actionColor}.main`,
                    color: `${actionColor}.contrastText`,
                    boxShadow: 2
                  }}
                >
                  {getEntityIcon(item.description.split(' ')[0])}
                </Avatar>
                
                {/* Action indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 1
                  }}
                >
                  {getActionIcon(item.action)}
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    position: 'relative',
                    '&:hover': {
                      boxShadow: 3,
                      borderColor: 'primary.main'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  {/* Time indicator */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'text.secondary',
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    <Schedule sx={{ fontSize: 14 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {format(item.timestamp, 'HH:mm')}
                    </Typography>
                  </Box>

                  <CardContent sx={{ pb: 2, pr: 8, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={2}>
                      {/* Header */}
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.3 }}>
                          {item.title}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {item.description}
                        </Typography>
                        
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(item.timestamp, { addSuffix: true })}
                        </Typography>
                      </Box>

                      {/* User and metadata chips */}
                      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                        <Chip
                          avatar={
                            <Avatar sx={{ width: 20, height: 20, bgcolor: 'primary.main' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                {item.user.name.charAt(0)}
                              </Typography>
                            </Avatar>
                          }
                          label={item.user.name}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontWeight: 500 }}
                        />
                        
                        {item.metadata?.TriggerType && (
                          <Chip
                            label={item.metadata.TriggerType}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        )}

                        {item.relatedEntities && item.relatedEntities.length > 0 && (
                          <Chip
                            label={`${item.relatedEntities.length} related`}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        )}
                      </Stack>

                      {/* Action Buttons */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1}>
                          {hasDetails && (
                            <Tooltip title={isExpanded ? "Show less" : "Show more details"}>
                              <IconButton
                                size="small"
                                onClick={() => toggleExpanded(item.id)}
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2)
                                  }
                                }}
                              >
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="View full details">
                            <IconButton
                              size="small"
                              onClick={() => onItemClick?.(item)}
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.info.main, 0.2)
                                }
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      {/* Expandable Details */}
                      <Collapse in={isExpanded}>
                        <Divider sx={{ mb: 2 }} />
                        
                        {/* Related Entities */}
                        {item.relatedEntities && item.relatedEntities.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                              Related Entities
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {item.relatedEntities.map((entity, idx) => (
                                <Chip
                                  key={idx}
                                  icon={getEntityIcon(entity.type)}
                                  label={`${entity.type}: ${entity.name || entity.id}`}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Changes */}
                        {item.changes && item.changes.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                              Changes Made
                            </Typography>
                            <Stack spacing={1.5}>
                              {item.changes.slice(0, 5).map((change, idx) => (
                                <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                                  <Stack spacing={2}>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 600, 
                                        color: 'primary.main'
                                      }}
                                    >
                                      {change.field}
                                    </Typography>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                      <Box 
                                        sx={{ 
                                          px: 1.5, 
                                          py: 1, 
                                          bgcolor: alpha(theme.palette.error.main, 0.1),
                                          borderRadius: 1,
                                          flex: 1,
                                          minWidth: 0
                                        }}
                                      >
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                          Previous
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-word',
                                            fontSize: '0.8rem'
                                          }}
                                        >
                                          {String(change.oldValue || '(empty)')}
                                        </Typography>
                                      </Box>
                                      <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>→</Typography>
                                      <Box 
                                        sx={{ 
                                          px: 1.5, 
                                          py: 1, 
                                          bgcolor: alpha(theme.palette.success.main, 0.1),
                                          borderRadius: 1,
                                          flex: 1,
                                          minWidth: 0
                                        }}
                                      >
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                          New
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-word',
                                            fontSize: '0.8rem'
                                          }}
                                        >
                                          {String(change.newValue || '(empty)')}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  </Stack>
                                </Paper>
                              ))}
                              {item.changes.length > 5 && (
                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                                  ... and {item.changes.length - 5} more changes
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                        
                        {/* Additional Information */}
                        {item.metadata?.InfoData && Object.keys(item.metadata.InfoData).length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                              Additional Information
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Stack spacing={1.5}>
                                {Object.entries(item.metadata.InfoData).slice(0, 6).map(([key, value]) => (
                                  <Stack key={key} direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '40%' }}>
                                      {key}:
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 500,
                                        maxWidth: '60%',
                                        textAlign: 'right',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {String(value)}
                                    </Typography>
                                  </Stack>
                                ))}
                                {Object.keys(item.metadata.InfoData).length > 6 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                                    ... and {Object.keys(item.metadata.InfoData).length - 6} more fields
                                  </Typography>
                                )}
                              </Stack>
                            </Paper>
                          </Box>
                        )}
                      </Collapse>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Fade>
    );
  }, [expandedItems, showDateHeaders, timelineItems, getEntityIcon, getActionIcon, toggleExpanded, onItemClick, theme.palette]);

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Loading timeline...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleRefresh}
            startIcon={<Refresh />}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Chip 
              label={`${timelineItems.length} events`} 
              size="small" 
              variant="outlined"
              color="primary"
            />
          </Stack>
        }
        action={
          <Tooltip title="Refresh Timeline">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        }
        sx={{ pb: 1 }}
      />
       
      <CardContent sx={{ pt: 0 }}>
        {timelineItems.length === 0 ? (
          <StatusCard
            variant="empty"
            title="No timeline events found"
            message="When activities occur, they will appear here in chronological order."
            layout="card"
          />
        ) : (
          <Box sx={{ maxHeight, overflow: 'auto', pr: 1 }}>
            <Stack spacing={0}>
              {timelineItems.map((item, index) => renderTimelineItem(item, index))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditTimelineViewer;